import * as gasContractationActions from "../actions/gasContractation";
import FormInstallation from "../components/FormInstallation";
import FormPerson from "../components/FormPerson";
import FormGasContract from "../components/gas/FormGasContract";
import FormPayment from "../components/FormPayment";
import FormConfirmation from "../components/FormConfirmation";
import Finish from "../components/Finish";
import * as collectFormDataForAPI from "../utils/collectDataAPI";
import { default as leadsService } from "../services/leads";
import { StepsName } from "../types/steps";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { 
  IStepConfirmationFormData, 
  IStepPersonFormData, 
} from "../types/stepsFormData";
import { isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "@/hooks";

const useGasSteps = () => {
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();

  const gasContractation = useAppSelector(state => state.gasContractation);

  //TODO: check dispatch(gasContractationActions.resetLeadsErrors());

  const handleGoBack = () => {
    const prevStepIdx = gasContractation.currentStep.index - 1;
    dispatch(gasContractationActions.resetLeadsErrors());
    dispatch(gasContractationActions.setLeadCurrentStep(
      prevStepIdx, steps[prevStepIdx].name
    ));
  };

  const handleGoForward = () => {
    const followStepIdx = gasContractation.currentStep.index + 1;
    dispatch(
      gasContractationActions.setLeadCurrentStep(
        followStepIdx, 
        steps[followStepIdx].name
      )
    );
  };

  // TODO: Pass number or string (step index or name)
  const handleGoToStep = (index: number) => {
    if (
      index <= Object.values(gasContractation.completedSteps).length && 
      gasContractation.latestStep !== steps.length-1 && 
      !gasContractation.submitting
    ) {
      dispatch(
        gasContractationActions.setLeadCurrentStep(
          index, 
          steps[index].name
        ));
    }
  }

  let steps = [
    {
      name: 'installation',
      label: 'common:text.contractation_about_installation',
      content: <FormInstallation
          type="gas"
          onSubmit={ async (values) => {
            
            const {leadsConsent, ...checkValues} = values;
            const oldLeadsConsent = gasContractation.leadsConsent;
            if (
              !isEqual(checkValues, gasContractation.steps.installation) ||
              leadsConsent != gasContractation.leadsConsent
            ) {
              
              // If leads send to the API
              if ( Settings.features.leads && 

                  // Has given consent to store leads and receive communications
                  (values.leadsConsent || 

                    // Edge case in which the user goes back one step 
                    // and unchecks leadConsent
                    (!values.leadsConsent && oldLeadsConsent)
                  )
              ) {
                try {
                  const {lead_id: leadId} = await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectInstallationFormData(values)
                  );
                  
                  await dispatch(
                    gasContractationActions.setLeadId(leadId)
                  );
                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }
              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                gasContractationActions.setInstallationData(values)
              );

            }

            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )

            await handleGoForward();

          }}
        />
    },
    {
      name: 'person',
      label: 'common:text.contractation_about_you',
      content: <FormPerson
          type="gas"
          onSubmit={async (values: IStepPersonFormData) => {
            
            if (!isEqual(values, gasContractation.steps.person)) {

              // If leads send to the API
              if ( Settings.features.leads && gasContractation.leadsConsent) {

                const elecPersonFormData = collectFormDataForAPI.collectPersonFormData(
                  values,
                  gasContractation.steps.installation.cups
                )

                try {
                  await leadsService.sendGasLeadStepData(
                    elecPersonFormData
                  );
                
                  if ( Settings.contractation?.shouldCheckDNI ) {
                    const { dni_valid } = await leadsService.checkLeadDNI(gasContractation.leadId)
                    if (!dni_valid) {
                      await dispatch(gasContractationActions.setLeadsErrors({
                        dniInvalid: true
                      }));
                      
                      return;
                    }
                  };

                  if( !!Settings.contractation?.shouldCheckMorosity ) {
                    const { scoring } = await leadsService.checkLeadScore(gasContractation.leadId);
                    if (!scoring) {
                      await dispatch(gasContractationActions.setLeadsErrors({
                        badScore: true
                      }));
      
                      return;
                    }
                  }

                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }

              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                gasContractationActions.setPersonData(values)
              );

            }

            // If there is no error
            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )

            await handleGoForward();

          }}

          onGoBack={handleGoBack}
        />
    },
    {
      name: 'gasContract',
      label: 'common:text.contractation_about_gas_contract',
      content: <FormGasContract
          type="gas"
          onSubmit={async (values) => {

            if (!isEqual(values, gasContractation.steps.gasContract)) {

              // If leads send to the API
              if ( Settings.features.leads && gasContractation.leadsConsent ) {
                try {

                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectGasContractFormData(
                      values
                    )
                  );
                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }
                
              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                gasContractationActions.setGasContractData(values)
              );

            }

            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )
          
            await handleGoForward();

          }}
          onGoBack={handleGoBack}
        />
    },
    {
      name: 'payment',
      label: 'common:text.contractation_about_payment',
      content: <FormPayment
          type="gas"
          onSubmit={async (values) => {

            if (!isEqual(values, gasContractation.steps.payment)) {
            
              // If leads send to the API
              if ( Settings.features.leads && gasContractation.leadsConsent ) {

                try {
                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectPaymentFormData(
                      values,
                      gasContractation.steps.installation.cups
                    )
                  );
                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }

              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }
              
              // Register values to Redux state
              await dispatch(
                gasContractationActions.setPaymentData(values)
              );
            }

            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )
            
            await handleGoForward();
          
          }}

          onGoBack={handleGoBack}
        />
    },
    {
      name: 'confirmation',
      label: 'common:text.contractation_confirmation',
      content: <FormConfirmation
          type="gas"
          onSubmit={async (values: IStepConfirmationFormData) => {

            dispatch(gasContractationActions.setConfirmationData(values));

            try {
              
              // If lead has not been created, must send all the steps info
              if (!(Settings.features.leads && gasContractation.leadsConsent)) {
                await leadsService.sendGasLeadStepData(
                  collectFormDataForAPI.collectGasFormData(
                    gasContractation.steps
                  )
                );
              }

              let gasRes = null;
              gasRes = await leadsService.confirmGasContractation(
                collectFormDataForAPI.collectConfirmationFormData(
                  values,
                  gasContractation.steps.gasContract.cups
                )
              );

              if (gasRes?.status == 'OK' ) {
                await dispatch(
                  gasContractationActions.receiveConfirmNewContractResponse(
                    gasRes
                  )
                );

                await dispatch(
                  gasContractationActions.setLeadStepCompleted(
                    gasContractation.currentStep.index
                  )
                )

                await handleGoForward();

              } else {

                dispatch(
                  gasContractationActions.receiveConfirmNewContractResponse(
                    {
                      result: {
                        error: true,
                        render: {
                          format: 'markdown',
                          body: 'common:text.contractation_connectivity_error'
                        }
                      },
                      status: 'ERROR'
                    }
                  )
                );
              }
            
            } catch (err) {

              dispatch(
                gasContractationActions.receiveConfirmNewContractResponse(
                  {
                    result: {
                      error: true,
                      render: {
                        format: 'markdown',
                        body: 'common:text.contractation_connectivity_error'
                      }
                    },
                    status: 'ERROR'
                  }
                )
              );
            }

          }}
          onGoBack={handleGoBack}
        />
    },
    {
      name: 'finish',
      label: 'common:text.contractation_confirmed',
      content: <Finish
          type="gas"
          onGoBack={handleGoBack}
        />
    }
  ];

  const removeStep = (stepName: StepsName) => {
    steps = steps.filter(step => step.name !== stepName);
  }

  if (
    Settings?.contractation?.businessHaltContractation && 
    gasContractation.company
  ) {
    removeStep('contract');
    removeStep('gasContract');
    removeStep('payment');
  }

  return {
    steps,
    handleGoToStep,
    handleGoBack
  }

}

export default useGasSteps;